import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import MarketingNav from '../components/layout/MarketingNav';
import { authService } from '../services/api';
import { Button, Card, Input } from '../components/ui';

const EyeIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
  </svg>
);

const EyeOffIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
  </svg>
);

const ForgotPassword = ({ initialMode = 'email' }) => {
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resetMode, setResetMode] = useState(initialMode);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const mode = params.get('mode');
    if (mode === 'username') {
      setResetMode('username');
      setSuccess(false);
      setError('');
    }
  }, [location.search]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (resetMode === 'email') {
        await authService.forgotPassword(email);
        setSuccess(true);
      } else if (resetMode === 'username') {
        await authService.forgotUsername(email);
        setSuccess(true);
      } else {
        if (newPassword.length < 8) {
          setError('Password must be at least 8 characters.');
          setLoading(false);
          return;
        }

        if (newPassword !== confirmPassword) {
          setError('Passwords do not match.');
          setLoading(false);
          return;
        }

        await authService.resetPasswordVerified({
          username,
          email,
          newPassword,
          confirmPassword
        });
        setSuccess(true);
      }
    } catch (err) {
      console.error('Failed to request password reset:', err);
      setError(err.response?.data?.message || 'Unable to reset password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <MarketingNav transparent={false} />
      <main className="flex min-h-screen items-center justify-center bg-neutral-50 px-6 py-12 mt-20">
        <div className="w-full max-w-md">
          <Card>
            <div className="text-center">
              <p className="text-sm font-semibold uppercase tracking-wide text-primary-600">MHDF Momentum</p>
              <h1 className="mt-2 text-3xl font-display font-semibold text-neutral-900">
                {resetMode === 'username' ? 'Forgot Username' : 'Forgot Password'}
              </h1>
              <p className="mt-2 text-neutral-500">
                {resetMode === 'email'
                  ? 'We will email you a reset link.'
                  : resetMode === 'username'
                    ? 'We will email your username to the registered address.'
                    : 'Verify your username and email to reset your password.'}
              </p>
            </div>

            {error && (
              <div className="mt-4 rounded-soft border border-error-200 bg-error-50 p-3 text-sm text-error-700">
                {error}
              </div>
            )}

            <div className="mt-6 flex gap-2">
              <Button
                type="button"
                variant={resetMode === 'email' ? 'primary' : 'ghost'}
                className="w-full"
                onClick={() => {
                  setResetMode('email');
                  setSuccess(false);
                  setError('');
                }}
              >
                Email Link
              </Button>
              <Button
                type="button"
                variant={resetMode === 'username' ? 'primary' : 'ghost'}
                className="w-full"
                onClick={() => {
                  setResetMode('username');
                  setSuccess(false);
                  setError('');
                }}
              >
                Forgot Username
              </Button>
              <Button
                type="button"
                variant={resetMode === 'verify' ? 'primary' : 'ghost'}
                className="w-full"
                onClick={() => {
                  setResetMode('verify');
                  setSuccess(false);
                  setError('');
                }}
              >
                Verify & Reset
              </Button>
            </div>

            {success ? (
              <div className="mt-6 rounded-soft border border-success-200 bg-success-50 p-4 text-sm text-success-700">
                {resetMode === 'email'
                  ? 'If an account exists for that email, a reset link has been sent. Please check your inbox.'
                  : resetMode === 'username'
                    ? 'If an account exists for that email, your username has been sent.'
                    : 'Password reset successful. You can now sign in.'}
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="mt-6 space-y-4">
                <Input
                  label="Email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  fullWidth
                  required
                />

                {resetMode === 'verify' && (
                  <>
                    <Input
                      label="Username"
                      name="username"
                      type="text"
                      autoComplete="username"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      fullWidth
                      required
                    />
                    <Input
                      label="New Password"
                      name="newPassword"
                      type={showNewPassword ? 'text' : 'password'}
                      autoComplete="new-password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      endIcon={showNewPassword ? EyeOffIcon : EyeIcon}
                      onEndIconClick={() => setShowNewPassword((prev) => !prev)}
                      fullWidth
                      required
                    />
                    <Input
                      label="Confirm Password"
                      name="confirmPassword"
                      type={showConfirmPassword ? 'text' : 'password'}
                      autoComplete="new-password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      endIcon={showConfirmPassword ? EyeOffIcon : EyeIcon}
                      onEndIconClick={() => setShowConfirmPassword((prev) => !prev)}
                      fullWidth
                      required
                    />
                  </>
                )}

                <Button type="submit" fullWidth disabled={loading} className="w-full">
                  {loading
                    ? 'Submitting...'
                    : resetMode === 'email'
                      ? 'Send Reset Link'
                      : resetMode === 'username'
                        ? 'Send Username'
                        : 'Reset Password'}
                </Button>
              </form>
            )}

            <p className="mt-4 text-center text-sm text-neutral-500">
              Remembered your password?{' '}
              <Link to="/login" className="font-semibold text-primary-600 hover:text-primary-700">
                Sign In
              </Link>
            </p>
          </Card>
        </div>
      </main>
    </>
  );
};

export default ForgotPassword;
